import {
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  ADAPTER_GUIDE_DEFAULT_WIDTH,
  ADAPTER_GUIDE_PUSH_MAX_WIDTH,
  ADAPTER_GUIDE_WIDTH_STORAGE_KEY,
  type AdapterGuideResizeKey,
  getAdapterGuideDragWidth,
  getAdapterGuideKeyboardWidth,
  getAdapterGuideReservedWidth,
  parseAdapterGuideWidth,
} from "./guideWidth";

interface AdapterGuideResizeStart {
  pointerId: number;
  startWidth: number;
  startX: number;
  previousCursor: string;
  previousUserSelect: string;
}

const readStoredWidth = (): number => {
  if (typeof window === "undefined") return ADAPTER_GUIDE_DEFAULT_WIDTH;
  try {
    return parseAdapterGuideWidth(window.localStorage.getItem(ADAPTER_GUIDE_WIDTH_STORAGE_KEY));
  } catch {
    return ADAPTER_GUIDE_DEFAULT_WIDTH;
  }
};

/**
 * The adapter guide's resizable width: drag or arrow keys on the resize handle, remembered in local storage.
 * Returns the width, the layout style variables and the handle's event props.
 */
export const useAdapterGuideResize = () => {
  const [guideWidth, setGuideWidth] = useState(readStoredWidth);
  const guideWidthRef = useRef(guideWidth);
  const resizeStartRef = useRef<AdapterGuideResizeStart | undefined>(undefined);

  const updateGuideWidth = useCallback((nextWidth: number, persist = false) => {
    guideWidthRef.current = nextWidth;
    setGuideWidth(nextWidth);
    if (!persist || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(ADAPTER_GUIDE_WIDTH_STORAGE_KEY, String(nextWidth));
    } catch {
      return;
    }
  }, []);

  // Restore the page cursor if the guide unmounts mid-drag.
  useEffect(
    () => () => {
      const resizeStart = resizeStartRef.current;
      if (!resizeStart || typeof document === "undefined") return;
      document.body.style.cursor = resizeStart.previousCursor;
      document.body.style.userSelect = resizeStart.previousUserSelect;
    },
    [],
  );

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    resizeStartRef.current = {
      pointerId: event.pointerId,
      startWidth: guideWidthRef.current,
      startX: event.clientX,
      previousCursor: document.body.style.cursor,
      previousUserSelect: document.body.style.userSelect,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const resizeStart = resizeStartRef.current;
    if (!resizeStart || resizeStart.pointerId !== event.pointerId) return;
    updateGuideWidth(getAdapterGuideDragWidth(resizeStart.startWidth, resizeStart.startX, event.clientX));
  };

  const onPointerEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
    const resizeStart = resizeStartRef.current;
    if (!resizeStart || resizeStart.pointerId !== event.pointerId) return;
    resizeStartRef.current = undefined;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    document.body.style.cursor = resizeStart.previousCursor;
    document.body.style.userSelect = resizeStart.previousUserSelect;
    updateGuideWidth(guideWidthRef.current, true);
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    updateGuideWidth(getAdapterGuideKeyboardWidth(guideWidthRef.current, event.key as AdapterGuideResizeKey), true);
  };

  const reservedGuideWidth = getAdapterGuideReservedWidth(guideWidth);
  return {
    guideWidth,
    guideLayoutStyle: {
      "--adapter-guide-width": `${guideWidth}px`,
      "--adapter-guide-reserved-width": `${reservedGuideWidth}px`,
    } as CSSProperties,
    guideOverlapsContent: guideWidth > ADAPTER_GUIDE_PUSH_MAX_WIDTH,
    resizeHandleProps: {
      onKeyDown,
      onPointerCancel: onPointerEnd,
      onPointerDown,
      onPointerMove,
      onPointerUp: onPointerEnd,
    },
  };
};
